import { useTranslation } from 'react-i18next'
import { PriceOrDiscountBlock } from '@/shared/components/cse'
import { isSafeExternalUrl } from '@/shared/lib/safeRedirect'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import type { MatchClub } from '@/features/match-event/schemas/matchEvent.schema'
import { HorizonMediaCard } from '../../../shared/components/HorizonMediaCard'

const ShieldIcon = createLazyThemedIcon('shield')

function clubActionTarget(club: MatchClub): { to: string; search?: Record<string, unknown> } {
  if (club.single_orderable_match_id) {
    return { to: `/match-event/${encodeURIComponent(club.single_orderable_match_id)}` }
  }

  return { to: '/match-event', search: { clubId: club.id } }
}

export function HorizonMatchClubCard({
  club,
  priority = false,
}: {
  club: MatchClub
  priority?: boolean
}) {
  const { t } = useTranslation()
  const { to, search } = clubActionTarget(club)
  const partnerLinks = club.partner_links?.filter((link) => isSafeExternalUrl(link.href)) ?? []

  return (
    <HorizonMediaCard
      to={to}
      search={search}
      title={club.name}
      subtitle={club.sport.name}
      imageUrl={club.logo_url}
      imageWebpUrl={club.logo_webp_url}
      imageObjectFit="contain"
      fallbackIcon={ShieldIcon}
      fallbackTone={club.color_primary ?? undefined}
      details={
        <>
          {club.stadium_name ? <p className="m-0">{club.stadium_name}</p> : null}
          {club.description ? <p className="mt-1 m-0 line-clamp-2 text-[var(--brand-text-primary)]">{club.description}</p> : null}
          {partnerLinks.length > 0 ? (
            <ul className="mt-2 flex list-none flex-wrap gap-x-4 gap-y-1 border-t border-[var(--brand-border-soft)] p-0 pt-2">
              {partnerLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--brand-primary)] underline-offset-2 hover:underline"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      }
      price={
        club.price_subsidized_cents != null ? (
          <PriceOrDiscountBlock
            bare
            label={t('match_event.price_label')}
            value={formatMoneyCents(club.price_subsidized_cents)}
            compareAt={club.price_public_cents != null ? formatMoneyCents(club.price_public_cents) : undefined}
            helper={t('match_event.price_helper')}
          />
        ) : undefined
      }
      actionLabel={t('match_event.view')}
      actionAriaLabel={t('match_event.clubs.view_club_aria', { name: club.name })}
      priority={priority}
    />
  )
}
